import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable, catchError, map } from 'rxjs';
import { MANUALTEST_FILETYPES } from '../../../core/constants/enums';
import { UploadFileResponse } from '../../../core/models/upload-file-response';
import { CreateTestplanRequest } from '../../../core/models/create-testplan-request';
import { Test, TestCaseTypeList } from '../../../core/models/test-case-type-list-response';
import { CreateTestCaseResponse } from '../../../core/models/create-test-case-response';
import { SummaryResponse } from '../../../core/models/summary-response';
import {
  AuditoryRequest,
  AuditoryResponse,
  TestPlanTemplate,
  InconsistencyRequest,
  InconsistencyResponse,
  Requirement,
  KnowledgeFolderRef,
  TestPlanSourceMetadata,
  KnowledgeUploadResponse
} from '../interfaces/manual-tests.interfaces';
import { UtilsService } from '../../../core/services/utils.service';

type HeadersConfig = Record<string, string>;

@Injectable({
  providedIn: 'root'
})
export class ManualtestsService {
  private readonly apiTestplan: string;
  private readonly apiTemplates: string;
  private readonly apiKnowledge: string;
  private readonly knowledgeResource = 'knowledge';
  private readonly defaultHeaders: HeadersConfig = {
    'Accept': 'application/json, text/plain, */*',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  };

  constructor(
    private readonly http: HttpClient,
    private readonly utilsService: UtilsService
  ) {
    // this.apiTestplan = this.normalizeApiUrl(environment.testplanApiUrl);
    this.apiTestplan = environment.testplanApiUrl;
    this.apiTemplates = `${this.apiTestplan}`;
    this.apiKnowledge = environment.knowledgeApiUrl;
  }

  private normalizeApiUrl(url: string): string {
    return url.replace('https:', 'http:');
  }

  private decodeRequirementTitles(summary: SummaryResponse): SummaryResponse {
    if (summary.requirements) {
      summary.requirements = summary.requirements.map(req => ({
        ...req,
        title: this.utilsService.decodeUnicodeEscapes(req.title),
        content: this.utilsService.decodeUnicodeEscapes(req.content)
      }));
    }
    return summary;
  }

  private getHeaders(): HttpHeaders {
    const headers: HeadersConfig = {
      ...this.defaultHeaders,
      'Content-Type': 'application/json',
      'x-genai-bearer': 'test' // TO-DO: temporalmente hasta que el back no pida este header
    };
    return new HttpHeaders(headers);
  }

  private normalizeUploadFileResponse(response: any): UploadFileResponse {
    const projectId = response?.projectId ?? response?.data?.projectId ?? '';

    const listCandidates = [
      response?.fileIds,
      response?.data?.fileIds,
      response?.uploadedFileIds,
      response?.data?.uploadedFileIds
    ];

    const listIds = listCandidates.find(Array.isArray) ?? [];

    const singleCandidates = [
      response?.fileId,
      response?.data?.fileId,
      response?.id,
      response?.data?.id
    ].filter((id: unknown): id is string => typeof id === 'string' && !!id);

    const fileIdsFromFilesArray = Array.isArray(response?.files)
      ? response.files
        .map((file: any) => file?.id)
        .filter((id: unknown): id is string => typeof id === 'string' && !!id)
      : [];

    const fileIds = Array.from(new Set([
      ...listIds.filter((id: unknown): id is string => typeof id === 'string' && !!id),
      ...singleCandidates,
      ...fileIdsFromFilesArray
    ]));

    return { projectId, fileIds };
  }

  uploadFile(files: File[], type: MANUALTEST_FILETYPES, projectId = ''): Observable<UploadFileResponse> {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('fileType', type);
    files.forEach(file => formData.append('fileList', file));

    const headers = new HttpHeaders({
      ...this.defaultHeaders,
      'Accept-Language': 'es-ES,es;q=0.9,en;q=0.8'
    });

    return this.http.post<any>(
      `${this.apiTestplan}/upload-file`,
      formData,
      { headers }
    ).pipe(
      map(response => {
        const normalized = this.normalizeUploadFileResponse(response);
        return {
          ...normalized,
          projectId: normalized.projectId || projectId
        };
      }),
      catchError(error => {
        console.error('Error uploading file:', error);
        throw error;
      })
    );
  }

  getSummary(projectId: string, model: string, documentType: string, fileIds: string[] = []): Observable<SummaryResponse> {
    return this.http.post<SummaryResponse>(
      `${this.apiTestplan}/summary`,
      { projectId, criteriaId: model, documentType, fileIds },
      { headers: this.getHeaders() }
    ).pipe(
      map(response => this.decodeRequirementTitles(response)),
      catchError(error => {
        console.error('Error getting summary:', error);
        throw error;
      })
    );
  }

  parse(createTestplanRequest: CreateTestplanRequest): Observable<TestCaseTypeList> {
    return this.http.post<TestCaseTypeList>(
      `${this.apiTestplan}/parse`,
      createTestplanRequest,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error parsing testplan:', error);
        throw error;
      })
    );
  }

  create(createTestplanRequest: CreateTestplanRequest | { projectId: string; criteriaId: string; testCaseIds: string[]; testCases?: Test[]; fileIds?: string[] }): Observable<any> {
    return this.http.post<any>(
      `${this.apiTestplan}/create-test-case`,
      createTestplanRequest,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error creating test case:', error);
        throw error;
      })
    );
  }

  getTaskProgress(taskId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiTestplan}/tasks/${taskId}/status`,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  auditRequirements(request: AuditoryRequest): Observable<AuditoryResponse> {
    return this.http.post<AuditoryResponse>(`${this.apiTestplan}/auditory`, request, { headers: this.getHeaders() });
  }

  // Templates
  getTemplatesByProject(projectId: string): Observable<TestPlanTemplate[]> {
    return this.http.get<any[]>(`${this.apiTemplates}/templates/project/${projectId}`)
      .pipe(
        map(templates => {
          const mappedTemplates = templates.map(template => ({
            id: template.id,
            userId: template.userId,
            template: template.template,
            title: template.templateName || template.title || `Template ${template.id}`
          } as TestPlanTemplate));
          return mappedTemplates;
        }),
        catchError(error => {
          console.error('Error fetching templates:', error);
          throw error;
        })
      );
  }

  uploadTemplate(file: File, model: string, projectId: string, title: string, criteriaId: string): Observable<void> {
    const formData = new FormData();
    formData.append('model', model);
    formData.append('projectId', projectId);
    formData.append('templateName', title);
    formData.append('criteriaId', criteriaId);
    formData.append('file', file, file.name);
    return this.http.post<void>(`${this.apiTemplates}/upload-template`, formData);
  }

  extractTemplate(file: File, model: string, projectId: string, title: string, criteriaId: string): Observable<void> {
    const formData = new FormData();
    formData.append('model', model);
    formData.append('projectId', projectId);
    formData.append('templateName', title);
    formData.append('criteriaId', criteriaId);
    formData.append('file', file, file.name);
    return this.http.post<void>(`${this.apiTemplates}/extract-template`, formData);
  }

  validateDocumentWithTemplate(file: File, templateId: string, model: string, criteriaId: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('model', model);
    formData.append('templateId', templateId);
    formData.append('criteriaId', criteriaId);

    return this.http.post<any>(`${this.apiTemplates}/template/validate`, formData);
  }

  getSummaryWithTemplate(file: File, templateId: string, model: string, criteriaId: string, projectId: string): Observable<SummaryResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('model', model);
    formData.append('templateId', templateId);
    formData.append('criteriaId', criteriaId);
    formData.append('projectId', projectId);

    return this.http.post<SummaryResponse>(`${this.apiTemplates}/template/summary`, formData)
      .pipe(
        map(response => this.decodeRequirementTitles(response)),
        catchError(error => {
          console.error('Error getting template summary:', error);
          throw error;
        })
      );
  }

  deleteTemplate(templateId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiTemplates}/template/delete/${templateId}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(error => {
        console.error('Error deleting template:', error);
        throw error;
      })
    );
  }

  stop(taskId: string): Observable<boolean>{
    return this.http.post<boolean>(`${this.apiTestplan}/stop`, {taskId});
  }

  parseMultiple(projectId: string, criteriaId: string, requirements: Requirement[], fileIds: string[] = []): Observable<any> {
    const request = {
      projectId,
      criteriaId,
      requirements,
      fileIds
    };

    return this.http.post<any>(
      `${this.apiTestplan}/parse-multiple`,
      request,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error parsing multiple testplans:', error);
        throw error;
      })
    );
  }

  downloadExampleFile(type: string): Observable<Blob> {
    return this.http.get(
      `${this.apiTestplan}/example-file/${type}/download`,
      {
        responseType: 'blob',
        headers: this.getHeaders()
      }
    ).pipe(
      catchError(error => {
        console.error('Error downloading example file:', error);
        throw error;
      })
    );
  }

  validateInconsistencies(request: InconsistencyRequest): Observable<InconsistencyResponse> {
    return this.http.post<InconsistencyResponse>(
      `${this.apiTestplan}/validate-inconsistencies`,
      request,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Sube un PDF generado por el agente Test Plan al endpoint de subida de ms-knowledge.
   * El JWT lo añade el HttpInterceptor global; NO se debe fijar Content-Type manualmente
   * (Angular construye automáticamente el multipart/form-data; boundary=…).
   */
  publishTestPlanToKnowledge(
    blob: Blob,
    fileName: string,
    projectId: string,
    folderId: string | null,
    sourceMetadata: TestPlanSourceMetadata
  ): Observable<KnowledgeUploadResponse> {
    const formData = new FormData();
    const pdfFile = new File([blob], fileName, { type: 'application/pdf' });
    formData.append('file', pdfFile, fileName);
    formData.append('projectId', projectId);
    if (folderId) {
      formData.append('folderId', folderId);
    }
    formData.append('sourceType', 'TESTPLAN');
    formData.append('sourceMetadata', JSON.stringify(sourceMetadata));

    return this.http.post<KnowledgeUploadResponse>(
      `${this.apiKnowledge}/${this.knowledgeResource}/files/upload-file`,
      formData
    );
  }

  /** Lista las carpetas de la base de conocimiento de un proyecto. */
  listKnowledgeFolders(projectId: string): Observable<KnowledgeFolderRef[]> {
    return this.http.get<KnowledgeFolderRef[]>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders?projectId=${projectId}`
    );
  }

  /** Crea una nueva carpeta en la base de conocimiento del proyecto. */
  createKnowledgeFolder(projectId: string, name: string): Observable<KnowledgeFolderRef> {
    return this.http.post<KnowledgeFolderRef>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders?projectId=${projectId}`,
      { name }
    );
  }
}
